import type { RefObject } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

import { createStorefrontScroll, isIOS } from "./storefront-scroll";

/**
 * 抽屉打开后晚这么久才把店面换成 body 滚动，等入口角色缩小淡出（280ms）播完。
 * 换的那一刻整页的滚动位置归零，iOS 会把正在播动画的固定定位入口画到上方几百像素处（模拟器录屏实测）；
 * 这时抽屉正在升起，它不受影响。
 */
const SWITCH_DELAY_MS = 300;

/** iOS 店面独立滚动；短内容拦截滚动穿透，手势期间不切换页面布局。挂件本身已由挂载入口放在 body 外。 */
export function useScrollContainment(
  host: RefObject<HTMLElement>,
  open: boolean,
  fullscreen: boolean,
) {
  const page = useRef<ReturnType<typeof createStorefrontScroll>>();
  const locked = useRef(fullscreen);
  locked.current = fullscreen;
  useLayoutEffect(() => {
    const doc = host.current?.ownerDocument;
    const win = doc?.defaultView;
    if (!open || !doc || !win || !isIOS(win)) return;
    const switching = win.setTimeout(() => {
      page.current = createStorefrontScroll(doc);
      page.current?.setLocked(locked.current);
    }, SWITCH_DELAY_MS);
    return () => {
      win.clearTimeout(switching);
      page.current?.dispose();
      page.current = undefined;
    };
  }, [host, open]);
  useLayoutEffect(() => {
    page.current?.setLocked(fullscreen);
  }, [open, fullscreen]);
  useLayoutEffect(() => {
    const container = host.current;
    if (!open || !container) return;
    const doc = container.ownerDocument;
    const win = doc.defaultView;
    if (!win) return;
    let inside = false;
    let scrollable: HTMLElement | undefined;
    let selecting = false;

    const start = (event: TouchEvent) => {
      const path = event.composedPath();
      inside = path.includes(container);
      scrollable = undefined;
      selecting = false;
      if (!inside) return;
      for (const target of path) {
        if (target === container) break;
        if (!(target instanceof HTMLElement)) continue;
        const selection = win.getSelection();
        selecting ||=
          !!selection &&
          !selection.isCollapsed &&
          selection.containsNode(target, true);
        selecting ||=
          target instanceof HTMLTextAreaElement &&
          target.matches(":focus") &&
          target.selectionStart !== target.selectionEnd;
        const style = win.getComputedStyle(target);
        if (
          /auto|scroll/.test(
            `${style.overflow} ${style.overflowX} ${style.overflowY}`,
          )
        ) {
          scrollable = target;
          break;
        }
      }
    };
    const move = (event: TouchEvent) => {
      if ((!inside && !fullscreen) || event.touches.length !== 1 || selecting)
        return;
      // WebKit #243452：没有溢出时 overscroll-behavior 不生效；长内容继续原生滚动。
      if (
        (!scrollable ||
          (scrollable.scrollHeight <= scrollable.clientHeight &&
            scrollable.scrollWidth <= scrollable.clientWidth)) &&
        event.cancelable
      ) {
        event.preventDefault();
      }
    };
    // 必须在手势开始前注册；composedPath 才能识别 Shadow DOM 内的触摸起点。
    doc.addEventListener("touchstart", start, { capture: true, passive: true });
    doc.addEventListener("touchmove", move, { capture: true, passive: false });
    return () => {
      doc.removeEventListener("touchstart", start, true);
      doc.removeEventListener("touchmove", move, true);
    };
  }, [host, open, fullscreen]);
}
